/**
 * Settings → Fields.
 *
 * Every column of every table, and what kind of thing it holds. Pick a table
 * on the left, set the type of each of its fields on the right.
 *
 * The type list is NOT hardcoded here — it comes from
 * `GET /api/fin/meta/attributes`, which serves the vocabulary declared in
 * api/_lib/attribute-types.ts. Adding a type server-side makes it appear here
 * with no frontend change.
 *
 * Only the fields the app owns can be retyped. A column the warehouse fills in
 * is shown greyed with its type, because retyping it would not change what the
 * next ETL run writes into it — it would only change how that value is read
 * back, which is a way to make a column lie.
 */

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Plus, X, Lock, AlertTriangle, Search } from 'lucide-react';

/**
 * The refusals from this endpoint carry the detail that makes them
 * actionable — which choices are still in use, why a linked record cannot be
 * set by hand — so the whole body is kept rather than just a message.
 */
interface ApiError { error?: string; hint?: string; stranded?: string[] }

async function call<T>(url: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem('fin_auth_token');
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers as Record<string, string> ?? {}),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiError;
    throw Object.assign(new Error(body.error ?? `HTTP ${res.status}`), { body });
  }
  return res.json() as Promise<T>;
}

interface TypeDef {
  id: string;
  name: string;
  help: string;
  attio: string;
  needsOptions?: boolean;
  multi?: boolean;
  needsCurrency?: boolean;
  needsReference?: boolean;
}

interface ObjectDef {
  slug: string;
  label: string;
  mode: 'editable' | 'overlay' | 'readonly';
  source_system: string | null;
  attribute_count: number;
}

interface AttrDef {
  object_slug: string;
  api_slug: string;
  label: string;
  type: string;
  ownership: 'source' | 'user' | 'derived';
  is_required: boolean;
  options: string[] | null;
  references_object: string | null;
  currency_code: string | null;
  help_text: string | null;
  type_updated_by: string | null;
  type_updated_at: string | null;
}

/** Offered in the currency picker. Anything else can be typed in. */
const COMMON_CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'AED', 'CYP'];

export function FieldsTab({ isAdmin }: { isAdmin: boolean }) {
  const [types, setTypes] = useState<TypeDef[]>([]);
  const [objects, setObjects] = useState<ObjectDef[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [attrs, setAttrs] = useState<AttrDef[] | null>(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    call<{ types: TypeDef[]; objects: ObjectDef[] }>('/api/fin/meta/attributes')
      .then((d) => {
        setTypes(d.types);
        setObjects(d.objects);
        setActive((a) => a ?? d.objects[0]?.slug ?? null);
      })
      .catch(() => toast.error('Could not load the field catalogue'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!active) return;
    setAttrs(null);
    call<{ attributes: AttrDef[] }>(`/api/fin/meta/attributes?object=${active}`)
      .then((d) => setAttrs(d.attributes))
      .catch(() => toast.error('Could not load those fields'));
  }, [active]);

  const shown = useMemo(() => {
    if (!attrs) return null;
    const q = filter.trim().toLowerCase();
    if (!q) return attrs;
    return attrs.filter((a) =>
      a.label.toLowerCase().includes(q) || a.api_slug.toLowerCase().includes(q));
  }, [attrs, filter]);

  const save = async (attr: AttrDef, patch: Partial<AttrDef>) => {
    const next = { ...attr, ...patch };
    try {
      await call('/api/fin/meta/attributes', {
        method: 'PATCH',
        body: JSON.stringify({
          object: attr.object_slug,
          attribute: attr.api_slug,
          type: next.type,
          label: next.label,
          options: next.options,
          currency_code: next.currency_code,
          help_text: next.help_text,
        }),
      });
      setAttrs((cur) => cur?.map((a) => (a.api_slug === attr.api_slug ? next : a)) ?? cur);
      toast.success(`${next.label} saved`);
    } catch (err) {
      // The endpoint refuses rather than half-applying — surface exactly why,
      // since "some rows still use choices you removed" is actionable and
      // "failed to save" is not.
      const body = (err as { body?: ApiError }).body;
      toast.error(body?.error ?? 'Could not save that field', {
        description: body?.stranded?.length
          ? `Still in use: ${body.stranded.join(', ')}`
          : body?.hint,
      });
    }
  };

  if (loading) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
      <nav className="flex max-h-[70vh] flex-col gap-0.5 overflow-y-auto pr-1">
        {objects.map((o) => (
          <button
            key={o.slug}
            type="button"
            onClick={() => setActive(o.slug)}
            className={cn(
              'flex items-center justify-between rounded-md px-2 py-1.5 text-left text-sm',
              o.slug === active ? 'bg-accent font-medium' : 'hover:bg-accent/50'
            )}
          >
            <span className="truncate">{o.label}</span>
            <span className="ml-2 shrink-0 text-xs text-muted-foreground">
              {o.attribute_count}
            </span>
          </button>
        ))}
      </nav>

      <section className="min-w-0">
        <div className="relative mb-3">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Find a field…"
            className="h-8 pl-7 text-sm"
          />
        </div>

        {!isAdmin && (
          <p className="mb-3 flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5 shrink-0" />
            A field type applies to everyone who reads the table, so only an
            administrator can change it.
          </p>
        )}

        {!shown ? (
          <Skeleton className="h-72 w-full" />
        ) : (
          <div className="divide-y rounded-md border">
            {shown.map((a) => (
              <FieldRow
                key={a.api_slug}
                attr={a}
                types={types}
                editable={isAdmin && a.ownership === 'user'}
                onSave={(patch) => save(a, patch)}
              />
            ))}
            {!shown.length && (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                No field matches “{filter}”.
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

// ── One field ───────────────────────────────────────────────────────────────

function FieldRow({
  attr, types, editable, onSave,
}: {
  attr: AttrDef;
  types: TypeDef[];
  editable: boolean;
  onSave: (patch: Partial<AttrDef>) => Promise<void>;
}) {
  const def = types.find((t) => t.id === attr.type);
  const [open, setOpen] = useState(false);
  const [draftType, setDraftType] = useState(attr.type);
  const [options, setOptions] = useState<string[]>(attr.options ?? []);
  const [currency, setCurrency] = useState(attr.currency_code ?? '');
  const [adding, setAdding] = useState('');
  const [busy, setBusy] = useState(false);

  const draftDef = types.find((t) => t.id === draftType);
  const needsChoices = draftDef?.needsOptions === true;
  const incomplete = needsChoices && options.length === 0;

  const commit = async () => {
    setBusy(true);
    await onSave({
      type: draftType,
      options: needsChoices ? options : null,
      currency_code: draftDef?.needsCurrency ? (currency || null) : null,
    });
    setBusy(false);
    setOpen(false);
  };

  const addOption = () => {
    const v = adding.trim();
    if (!v || options.includes(v)) { setAdding(''); return; }
    setOptions([...options, v]);
    setAdding('');
  };

  return (
    <div className="px-3 py-2">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{attr.label}</p>
          <p className="truncate font-mono text-[11px] text-muted-foreground">{attr.api_slug}</p>
        </div>

        {attr.ownership !== 'user' && (
          <Badge variant="outline" className="shrink-0 gap-1 text-[10px] font-normal">
            <Lock className="h-2.5 w-2.5" />
            {attr.ownership === 'source' ? 'From the source system' : 'Calculated'}
          </Badge>
        )}

        {needsChoices && attr.options?.length ? (
          <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
            {attr.options.length} choices
          </span>
        ) : null}

        {attr.type === 'currency' && (
          <Badge variant="secondary" className="shrink-0 text-[10px]">
            {attr.currency_code ?? 'no currency'}
          </Badge>
        )}

        <Button
          variant={open ? 'secondary' : 'outline'}
          size="sm"
          disabled={!editable}
          onClick={() => setOpen((o) => !o)}
          className="h-7 w-40 shrink-0 justify-start text-xs"
          title={editable ? undefined : 'Only fields the app owns can be retyped'}
        >
          {def?.name ?? attr.type}
        </Button>
      </div>

      {open && (
        <div className="mt-3 space-y-3 rounded-md bg-muted/40 p-3">
          <div>
            <Label className="text-xs">What kind of thing is this?</Label>
            <Select value={draftType} onValueChange={setDraftType}>
              <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem key={t.id} value={t.id} className="text-sm">
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {draftDef && (
              <p className="mt-1 text-xs text-muted-foreground">{draftDef.help}</p>
            )}
          </div>

          {draftDef?.needsCurrency && (
            <div>
              <Label className="text-xs">Which currency?</Label>
              <div className="mt-1 flex flex-wrap gap-1">
                {COMMON_CURRENCIES.map((c) => (
                  <Button
                    key={c} type="button" size="sm"
                    variant={currency === c ? 'default' : 'outline'}
                    className="h-7 text-xs"
                    onClick={() => setCurrency(c)}
                  >
                    {c}
                  </Button>
                ))}
                <Input
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
                  placeholder="Other"
                  className="h-7 w-20 text-xs uppercase"
                />
              </div>
              {!currency && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-500">
                  Without a currency the amount shows as a plain number.
                </p>
              )}
            </div>
          )}

          {needsChoices && (
            <div>
              <Label className="text-xs">
                {draftDef?.multi ? 'Labels staff can pick from' : 'Choices in the dropdown'}
              </Label>
              <div className="mt-1 flex flex-wrap gap-1">
                {options.map((o) => (
                  <Badge key={o} variant="secondary" className="gap-1 pr-1 font-normal">
                    {o}
                    <button
                      type="button"
                      aria-label={`Remove ${o}`}
                      onClick={() => setOptions(options.filter((x) => x !== o))}
                      className="rounded-sm hover:bg-background/60"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {!options.length && (
                  <span className="text-xs italic text-muted-foreground">Nothing to pick yet</span>
                )}
              </div>
              <div className="mt-2 flex gap-1">
                <Input
                  value={adding}
                  onChange={(e) => setAdding(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); addOption(); }
                  }}
                  placeholder="Add a choice and press Enter"
                  className="h-7 text-xs"
                />
                <Button type="button" size="sm" variant="outline" className="h-7 shrink-0"
                  onClick={addOption}>
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                The order here is the order staff see. Removing a choice that rows
                still use is refused — change those rows first.
              </p>
            </div>
          )}

          {incomplete && (
            <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-500">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              Add at least one choice, or the cell will have nothing to offer.
            </p>
          )}

          {attr.type_updated_by && (
            <p className="text-[11px] text-muted-foreground">
              Last changed by {attr.type_updated_by}
              {attr.type_updated_at
                && ` on ${new Date(attr.type_updated_at).toLocaleDateString('en-GB',
                  { day: '2-digit', month: 'short', year: 'numeric' })}`}
            </p>
          )}

          <Separator />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" disabled={busy || incomplete} onClick={commit}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
